import React, { useMemo, useState } from "react";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import {
  IconButton,
  Badge,
  Popover,
  Tooltip,
  Box,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import { IGroup, IOrganizationUnit } from "@nendaTypes/NendaTypes";
import { t } from "i18next";
import { ensureArray } from "@shared/arrayUtils";

export type FilterKey = "group" | "premise";

interface FilterConfig {
  key: FilterKey;
  label: string;
  multi: boolean;
  options: { value: string; label: string }[];
  enabled: boolean;
}

interface ScreenSelectFilterProps {
  groups?: IGroup[];
  premises?: IOrganizationUnit[];
  values: Record<string, string[]>;
  onChange: (key: FilterKey, value: string[]) => void;
}

const ScreenSelectFilter: React.FC<ScreenSelectFilterProps> = ({
  groups = [],
  values,
  premises = [],
  onChange,
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        key: "group",
        label: t("customerportal.pages.schedule.filter.group"),
        multi: true,
        enabled: groups.length > 0,
        options: groups.map((g) => ({ value: g._id, label: g.name })),
      },
      {
        key: "premise",
        label: t("customerportal.pages.schedule.filter.premise"),
        multi: false,
        enabled: premises.length > 0,
        options: premises.map((p) => ({
          value: p._id,
          label: p.name,
        })),
      },
    ],
    [groups, premises, t]
  );

  const enabledFilterConfigs = filterConfigs.filter((config) => config.enabled);
  const enabledFiltersWithOptions = enabledFilterConfigs.filter(
    (config) => config.options.length > 0
  );

  if (enabledFiltersWithOptions.length === 0) {
    return null;
  }

  const handleSelect =
    (key: FilterKey) => (e: SelectChangeEvent<string | string[]>) => {
      const value = e.target.value;
      const valueArr = ensureArray(value).filter((v) => v !== "");
      onChange(key, valueArr);
    };

  const activeCount = enabledFilterConfigs.reduce(
    (acc, { key }) => acc + (values[key]?.length ? 1 : 0),
    0
  );

  return (
    <>
      <Tooltip title={t("customerportal.pages.schedule.filter.tooltip")}>
        <Badge badgeContent={activeCount} color="primary" overlap="circular">
          <IconButton
            size="small"
            sx={{ color: theme.palette.text.primary }}
            onClick={(e) => setAnchorEl(e.currentTarget)}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Badge>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            className: "MuiDataGrid-menuPaper",
            sx: {
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              minWidth: 300,
              backgroundColor: theme.palette.background.paper,
            },
          },
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>
            {t("customerportal.pages.schedule.filter.title") || "Filtrera"}
          </Typography>

          {enabledFiltersWithOptions.map(({ key, label, options, multi }) => {
            const current = values[key] ?? [];
            const isActive = current.length > 0;

            return (
              !!options.length && (
                <Box key={key} display="flex" alignItems="center" gap={1}>
                  <IconButton
                    size="small"
                    onClick={() => onChange(key, [])}
                    disabled={!isActive}
                    sx={{
                      mr: 0.5,
                      color: isActive ? "primary.main" : "text.disabled",
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>

                  <Typography variant="body2" sx={{ minWidth: 80, mr: 1 }}>
                    {label}
                  </Typography>

                  <FormControl variant="standard" sx={{ flex: 1 }}>
                    <Select
                      multiple={multi}
                      labelId={`${key}-filter-label`}
                      value={multi ? current : (current[0] ?? "")}
                      onChange={handleSelect(key)}
                      renderValue={(selected) => {
                        if (!multi) {
                          const opt = options.find((o) => o.value === selected);
                          return opt ? opt.label : "";
                        }
                        const sel = selected as string[];
                        if (sel.length === 0) return "";
                        if (sel.length === 1) {
                          const o = options.find((x) => x.value === sel[0]);
                          return o ? o.label : sel[0];
                        }
                        return `${sel.length} ${
                          t(
                            "customerportal.pages.schedule.filter.multiple_selected"
                          ) || "selected"
                        }`;
                      }}
                    >
                      {options.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )
            );
          })}
        </Box>
      </Popover>
    </>
  );
};

export default ScreenSelectFilter;
